// FileViewerToolBar.jsx
// This file contains the toolbar for the File Viewer component, providing various functionalities such as zooming, saving screenshots, and toggling different layout elements.

// Copyright HS Analysis GmbH, 2022
// Author: Jakob Burghardt, Viktor Eberhardt

//Framework imports
import React from "react";
import PropTypes from "prop-types";

// MUI imports
import { faCamera } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { makeStyles } from "@mui/styles";
import Fade from "@mui/material/Fade";
import IconButton from "@mui/material/IconButton";
import Paper from "@mui/material/Paper";
import Popover from "@mui/material/Popover";
import Tooltip from "@mui/material/Tooltip";

// Icon imports
import ViewQuilt from "@mui/icons-material/ViewQuilt";
import ZoomIn from "@mui/icons-material/ZoomIn";
import ZoomOut from "@mui/icons-material/ZoomOut";
import ZoomOutMapIcon from "@mui/icons-material/ZoomOutMap";
import LinkIcon from "@mui/icons-material/Link";
import LinkOffIcon from "@mui/icons-material/LinkOff";

// HSA imports
import { FileViewerContext } from "../FileViewer";
import { FileViewerTools } from "../FileViewer";
import Backend from "../../common/utils/Backend";
import HSAIcon from "../../common/utils/HSAIcon";
import RotationDialog from "./RotationDialog";
import ScreenshotDialog from "./ScreenshotDialog";
import ToggleButton from "../../viewer/components/ToggleButton";
import ToggleButtonFV from "./ToggleButtonFV";

const useStyles = makeStyles(() => ({
  toolbar: {
    position: "relative",
    margin: 0,
    padding: 0,
    width: "40px",
    background: "#fff",
    height: "100%",
    display: "grid",
    gridTemplateRows: "1fr auto",
    overflowX: "hidden",
    overflowY: "auto",
  },
  smallPaper: {
    height: 40,
  },
  toolbarTop: {}, // needed for js
  toolbarBottom: {}, // needed for js
  buttonStyle: {
    width: "40px",
    height: "40px",
    margin: 0,
    display: "inline-block",
    padding: 0,
    fontSize: "22px",
  },
  textButtonStyle: {
    width: "40px",
    height: "40px",
    margin: 0,
    display: "inline-block",
    padding: 0,
    fontSize: 18,
    fontWeight: "bold",
  },
  toolbarButtonIcon: {
    verticalAlign: "-4px",
    display: "inline-block",
  },
  toolbarButton: {
    display: "inline-block",
    width: 40,
    height: 40,
    padding: 0,
    margin: 0,
    fontSize: 22,
  },
}));

/*
Function to place and to style the buttons 
*/
function FileViewerToolbar(props) {
  const classes = useStyles(); // Use the styles
  const FV = React.useContext(FileViewerContext);
  const [openAdjust, setOpenAdjust] = React.useState(false);
  const [anchorEl, setAnchorEl] = React.useState(null);
  const [toolbarWidth, setToolbarWidth] = React.useState(40);
  const overflowHeightRef = React.useRef(9999); // Use a ref to store the overflow height
  const [openRotationDialog, setOpenRotationDialog] = React.useState(false);
  const [openScreenshotDialog, setOpenScreenshotDialog] = React.useState(false);
  // Keep latest values without retriggering effects
  const isFileSelectedRef = React.useRef(props.isFileSelected);
  const onSaveRef = React.useRef(FV.onSave);
  React.useEffect(() => {
    isFileSelectedRef.current = props.isFileSelected;
  }, [props.isFileSelected]);
  React.useEffect(() => {
    onSaveRef.current = FV.onSave;
  }, [FV.onSave]);

  // Memoized resize handler
  const handleResize = React.useCallback(() => {
    const toolbarElement = document.querySelector(`.${classes.toolbar}`);
    if (!toolbarElement) return;
    const overflowHeight = overflowHeightRef.current;
    const toolbarHeight = toolbarElement.clientHeight;
    const newWidth = toolbarHeight >= overflowHeight ? 40 : 96;
    setToolbarWidth((prev) => (prev !== newWidth ? newWidth : prev));
  }, [classes.toolbar]);

  // Mount-only: register listeners; use refs to access latest values to avoid effect churn
  React.useEffect(() => {
    initOverflowHeight();
    handleResize();
    const onResize = () => handleResize();
    window.addEventListener("resize", onResize);
    const handleKeyDown = (e) => {
      if ((e.ctrlKey || e.metaKey) && e.key === "s") {
        e.preventDefault();
        if (isFileSelectedRef.current && onSaveRef.current) onSaveRef.current();
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => {
      window.removeEventListener("resize", onResize);
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, []);

  /**
   * Initializes the overflow height by calculating the combined heights
   * of the toolbar's top and bottom elements, and stores the result in
   * the overflowHeightRef. If any of the required elements are not found,
   * the function exits early.
   */
  const initOverflowHeight = () => {
    const top = document.querySelector(`.${classes.toolbarTop}`);
    const bottom = document.querySelector(`.${classes.toolbarBottom}`);
    if (!top || !bottom) return;
    overflowHeightRef.current = top.clientHeight + bottom.clientHeight;
  };

  /**
   * Handles resizing of the toolbar by adjusting its width based on the current height.
   * If the toolbar's height is greater than or equal to the overflow height, sets a compact width.
   * Otherwise, sets a wider width to accommodate additional content.
   * The wider width is also letting room for a potential scrollbar if the content overflows.
   */
  // (legacy handleResize removed, using memoized version above)

  const [hasTimeBar, hasZStack] = React.useMemo(() => {
    if (!props.rendererRef) return [false, false];
    if (FV.splitscreenFileIds.length > 1) {
      let hasTs = false;
      let hasZs = false;
      for (const fileId of FV.splitscreenFileIds) {
        const omeDictEntry = FV.rendererDict[fileId]?.props?.omeDict?.[fileId];
        if (!omeDictEntry || !omeDictEntry.props || !omeDictEntry.props.ome)
          continue;
        const ome = omeDictEntry.props.ome;
        if (ome.sizeT > 1) hasTs = true;
        if (ome.sizeZ > 1) hasZs = true;
      }
      return [hasTs, hasZs];
    } else {
      return [
        props.rendererRef.props.ome.sizeT > 1,
        props.rendererRef.props.ome.sizeZ > 1,
      ];
    }
  }, [props.rendererRef, FV.splitscreenFileIds]);

  const toggleChainAllScreens = () => {
    if (FV.chainListFileIds.length > 1) {
      FV.setChainListFileIds([]);
    } else {
      FV.setChainListFileIds(FV.splitscreenFileIds);
    }
  };

  return (
    <div style={{ width: toolbarWidth }} className={classes.toolbar}>
      <div>
        <div className={classes.toolbarTop}>
          <Popover
            open={openAdjust}
            anchorEl={anchorEl}
            onClose={() => setOpenAdjust(false)}
            anchorOrigin={{
              vertical: "center",
              horizontal: "left",
            }}
            transformOrigin={{
              vertical: "center",
              horizontal: "right",
            }}
            TransitionComponent={Fade}
            TransitionProps={{ timeout: 350 }}
          >
            <Paper className={classes.smallPaper}>
              <ToggleButton
                title={"Histogram"}
                name={"showHistogram"}
                value={FV.isHistogramVisible}
                icon={"faChartArea"}
                onToggle={() => FV.toggleHistogram()}
              />
              {props.rendererRef && (
                <>
                  <ToggleButton
                    title={"Time Bar"}
                    icon={"faStopwatch"}
                    disabled={!hasTimeBar}
                    disableReason="No time series found"
                    value={FV.layoutStateObject["timeBar"]}
                    onToggle={() => {
                      if (!hasTimeBar) {
                        console.info(
                          "Time Bar cannot be shown because this image has no time series data"
                        );
                        return;
                      }
                      FV.toggleLayoutElement("timeBar");
                    }}
                  />

                  <ToggleButton
                    title={"Z-Stack"}
                    icon={"faLayerGroup"}
                    disabled={!hasZStack}
                    disableReason="No Z-stack found"
                    value={FV.layoutStateObject["zStackBar"]}
                    onToggle={() => {
                      if (!hasZStack) {
                        console.info(
                          "Z-Stack bar cannot be shown because this image has no Z-stack data"
                        );
                        return;
                      }
                      FV.toggleLayoutElement("zStackBar");
                    }}
                  />

                  <ToggleButton
                    title={"Scale Bar"}
                    icon={"faRuler"}
                    disabled={!props.rendererRef.props.ome.physicalSizeX}
                    value={FV.layoutStateObject["scaleBar"]}
                    onToggle={() => {
                      FV.toggleLayoutElement("scaleBar");
                    }}
                  />

                  <ToggleButton
                    title={"Minimap"}
                    icon={"MapIcon"}
                    value={FV.layoutStateObject["miniMap"]}
                    onToggle={() => {
                      FV.toggleLayoutElement("miniMap");
                    }}
                  />

                  <ToggleButton
                    title={"Image Info"}
                    icon={"faInfo"}
                    value={FV.layoutStateObject["imageInfo"]}
                    onToggle={() => {
                      FV.toggleLayoutElement("imageInfo");
                    }}
                  />
                  {props.rendererRef?.props.ome.physicalSizeX !== 1 && (
                    <ToggleButton
                      title={"Zoom"}
                      icon={"LinearScaleIcon"}
                      value={FV.layoutStateObject["zoomBar"]}
                      onToggle={() => {
                        FV.toggleLayoutElement("zoomBar");
                      }}
                    />
                  )}
                  <ToggleButton
                    title={"Label"}
                    icon={"faQrcode"}
                    value={FV.layoutStateObject["slideLabel"]}
                    onToggle={() => {
                      FV.toggleLayoutElement("slideLabel");
                    }}
                  />
                  <ToggleButton
                    title={"Macro Image"}
                    icon={"ImageSearchIcon"}
                    value={FV.layoutStateObject["macroImage"]}
                    onToggle={() => {
                      FV.toggleLayoutElement("macroImage");
                    }}
                  />
                  <ToggleButton
                    title={"File Navigation"}
                    disabled={FV.splitscreenFileIds.length > 1}
                    icon={"UnfoldMore"}
                    value={FV.layoutStateObject["fileNavButtons"]}
                    onToggle={() => {
                      FV.toggleLayoutElement("fileNavButtons");
                    }}
                    disableReason="Not available for splitscreen mode"
                  />
                </>
              )}
            </Paper>
          </Popover>
          <Tooltip placement="top" title="Layout" disableinteractive="true">
            <IconButton
              className={classes.toolbarButton}
              size="small"
              onClick={(e) => {
                setOpenAdjust(!openAdjust);
                setAnchorEl(e.target);
              }}
            >
              <ViewQuilt className={classes.toolbarButtonIcon} />
            </IconButton>
          </Tooltip>
          {FV.splitscreenFileIds.length > 1 && (
            <Tooltip
              disableInteractive
              placement="left"
              title={
                FV.chainListFileIds.length > 1
                  ? "Chain all Screens"
                  : "Unchain all Screens"
              }
            >
              <span>
                <IconButton
                  className={classes.toolbarButton}
                  onClick={toggleChainAllScreens}
                  size="large"
                >
                  {FV.chainListFileIds.length > 1 ? (
                    <LinkIcon style={{ color: "#0673C1" }} />
                  ) : (
                    <LinkOffIcon />
                  )}
                </IconButton>
              </span>
            </Tooltip>
          )}
        </div>
      </div>
      <div>
        <div className={classes.toolbarBottom}>
          <hr></hr>
          <Tooltip
            disableInteractive
            placement="left"
            title="Zoom Heatmap [Shift] + [H]"
          >
            <IconButton
              id="toolHeatmapBtn"
              className={classes.toolbarButton}
              onClick={() => FV.toggleActiveTool(FileViewerTools.ViewHeatMap)}
              size="large"
            >
              <HSAIcon
                icon="ViewHeatmapTool"
                active={FV.activeTool === FileViewerTools.ViewHeatMap}
                size={28}
              />
            </IconButton>
          </Tooltip>
          <Tooltip
            disableinteractive="true"
            placement="left"
            title="Comments [Shift] + [C]"
          >
            <IconButton
              className={classes.buttonStyle}
              onClick={() => FV.toggleActiveTool(FileViewerTools.Comment)}
              size="large"
            >
              <HSAIcon
                icon="CommentTool"
                active={FV.activeTool === FileViewerTools.Comment}
              />
            </IconButton>
          </Tooltip>
          <Tooltip disableinteractive="true" placement="left" title="Streaming">
            <IconButton
              className={classes.buttonStyle}
              onClick={() => FV.toggleActiveTool(FileViewerTools.Stream)}
              size="large"
            >
              <HSAIcon
                icon="Cast"
                active={FV.activeTool === FileViewerTools.Stream}
              />
            </IconButton>
          </Tooltip>
          <hr />

          <Tooltip
            disableinteractive="true"
            placement="left"
            title="Snapshot [Shift] + [P]"
          >
            <span>
              <IconButton
                disabled={!props.isFileSelected}
                onClick={() => {
                  setOpenScreenshotDialog(true);
                }}
                className={classes.buttonStyle}
              >
                <FontAwesomeIcon icon={faCamera} />
              </IconButton>
            </span>
          </Tooltip>
          <Tooltip
            disableinteractive="true"
            placement="left"
            title="Zoom 100% [Shift] + [+]"
          >
            <span>
              <IconButton
                disabled={!props.isFileSelected}
                onClick={() => {
                  props.triggerZoomAction("oneToN", 1);
                }}
                className={classes.textButtonStyle}
              >
                <span>{"1:1"}</span>
              </IconButton>
            </span>
          </Tooltip>
          <Tooltip
            disabledisableinteractive="true"
            placement="left"
            title="Zoom 25%"
          >
            <span>
              <IconButton
                disabled={!props.isFileSelected}
                onClick={() => {
                  props.triggerZoomAction("oneToN", 0.25);
                }}
                className={classes.textButtonStyle}
              >
                <span>{"1:4"}</span>
              </IconButton>
            </span>
          </Tooltip>
          <Tooltip disableinteractive="true" placement="left" title="Zoom 10%">
            <span>
              <IconButton
                disabled={!props.isFileSelected}
                onClick={() => {
                  props.triggerZoomAction("oneToN", 0.1);
                }}
                className={classes.textButtonStyle}
              >
                <span>{"1:10"}</span>
              </IconButton>
            </span>
          </Tooltip>
          <Tooltip
            disableinteractive="true"
            placement="left"
            title="Zoom fit [Shift] + [0]"
          >
            <span>
              <IconButton
                disabled={!props.isFileSelected}
                className={classes.buttonStyle}
                onClick={() => {
                  props.triggerZoomAction("zoomFit", 0);
                }}
              >
                <ZoomOutMapIcon />
              </IconButton>
            </span>
          </Tooltip>
          <div>
            <div>
              <Tooltip
                disableinteractive="true"
                placement="left"
                title="Zoom In [+]"
              >
                <span>
                  <IconButton
                    disabled={!props.isFileSelected}
                    onClick={() => {
                      props.triggerZoomAction("increase", 0);
                    }}
                    className={classes.buttonStyle}
                  >
                    <ZoomIn />
                  </IconButton>
                </span>
              </Tooltip>
              <Tooltip
                disableinteractive="true"
                placement="left"
                title="Zoom Out [-]"
              >
                <span>
                  <IconButton
                    disabled={!props.isFileSelected}
                    onClick={() => {
                      props.triggerZoomAction("decrease", 0);
                    }}
                    className={classes.buttonStyle}
                  >
                    <ZoomOut />
                  </IconButton>
                </span>
              </Tooltip>
            </div>
          </div>
          <div>
            <Tooltip
              disableinteractive="true"
              placement="left"
              title="Rotate 90°"
            >
              <IconButton
                id="RotatedRightBtn"
                className={classes.buttonStyle}
                onClick={() => {
                  FV.onRotate(90);
                }}
                size="large"
              >
                <HSAIcon icon="Rotate90Icon" />
              </IconButton>
            </Tooltip>
          </div>

          <div>
            <Tooltip
              disableinteractive="true"
              placement="left"
              title="Free Rotation"
            >
              <IconButton
                id="RotatedRightBtn"
                className={classes.buttonStyle}
                onClick={() => {
                  setOpenRotationDialog(true);
                }}
                size="large"
              >
                <HSAIcon icon="Rotate1Icon" />
              </IconButton>
            </Tooltip>
          </div>
          {props.updateDimensions && (
            <ToggleButtonFV updateDimensions={props.updateDimensions} />
          )}
          <hr></hr>
          <Tooltip
            disableinteractive="true"
            placement="left"
            title="Save [Ctrl] + [S]"
          >
            <span>
              <IconButton
                disabled={!props.isFileSelected}
                className={classes.buttonStyle}
                style={{
                  paddingLeft: 2,
                }}
                onClick={FV.onSave}
                size="large"
              >
                <HSAIcon icon="SaveTool" active={props.isFileSelected} />
              </IconButton>
            </span>
          </Tooltip>

          <RotationDialog
            open={openRotationDialog}
            fileId={FV.activeFileId}
            onClose={() => setOpenRotationDialog(false)}
            src={Backend.renderThumbnail(encodeURIComponent(FV.activeFileId))}
            // Pass metadata and renderer state; dialog derives presets internally
            ome={props.rendererRef?.props?.ome}
            rotState={
              (props.rendererRef &&
                typeof props.rendererRef.state?.rotState === "number" &&
                props.rendererRef.state.rotState) ||
              0
            }
            onConfirm={(rotationObject) => {
              FV.onImportRotation(rotationObject);
            }}
          />

          <ScreenshotDialog
            open={openScreenshotDialog}
            onClose={() => setOpenScreenshotDialog(false)}
          />
        </div>
      </div>
    </div>
  );
}

FileViewerToolbar.propTypes = {
  isFileSelected: PropTypes.bool,
  rendererRef: PropTypes.object,
  splitscreenCount: PropTypes.number,
  triggerZoomAction: PropTypes.func,
  updateDimensions: PropTypes.func,
};

export default FileViewerToolbar;
